<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useToggle } from '@vueuse/core';
import { useStoreGetters, useMapGetter } from 'dashboard/composables/store';

import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import CampaignLayout from 'dashboard/components-next/Campaigns/CampaignLayout.vue';
import CampaignList from 'dashboard/components-next/Campaigns/Pages/CampaignPage/CampaignList.vue';
import SMSCampaignDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/SMSCampaign/SMSCampaignDialog.vue';
import ConfirmDeleteCampaignDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/ConfirmDeleteCampaignDialog.vue';
import SMSCampaignEmptyState from 'dashboard/components-next/Campaigns/EmptyState/SMSCampaignEmptyState.vue';

import { INBOX_TYPES } from 'dashboard/helper/inbox';
import { CAMPAIGN_TYPES } from 'shared/constants/campaign';

const { t } = useI18n();
const getters = useStoreGetters();

const selectedCampaign = ref(null);
const [showDialog, toggleDialog] = useToggle();

const uiFlags = useMapGetter('campaigns/getUIFlags');
const isFetchingCampaigns = computed(() => uiFlags.value.isFetching);

const confirmDeleteRef = ref(null);

const campaigns = computed(() => {
  return getters['campaigns/getCampaigns'].value(
    CAMPAIGN_TYPES.ONE_OFF,
    [INBOX_TYPES.ZALO]
  );
});

const hasNoCampaigns = computed(
  () => campaigns.value?.length === 0 && !isFetchingCampaigns.value
);

const handleDelete = campaign => {
  selectedCampaign.value = campaign;
  confirmDeleteRef.value.dialogRef.open();
};
</script>

<template>
  <CampaignLayout
    header-title="Zalo Campaigns"
    button-label="Create Campaign"
    @click="toggleDialog()"
    @close="toggleDialog(false)"
  >
    <template #action>
      <SMSCampaignDialog
        v-if="showDialog"
        :inbox-types="[INBOX_TYPES.ZALO]"
        @close="toggleDialog(false)"
      />
    </template>
    <div v-if="isFetchingCampaigns" class="flex items-center justify-center py-10 text-n-slate-11">
      <Spinner />
    </div>
    <CampaignList
      v-else-if="!hasNoCampaigns"
      :campaigns="campaigns"
      @delete="handleDelete"
    />
    <SMSCampaignEmptyState
      v-else
      title="No Zalo Campaigns"
      subtitle="Create a campaign to send messages to Zalo contacts"
      class="pt-14"
    />
    <ConfirmDeleteCampaignDialog
      ref="confirmDeleteRef"
      :selected-campaign="selectedCampaign"
    />
  </CampaignLayout>
</template>
